import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { BalanceDto } from "../../lib/accounts";
import { Modal } from "../ui/Modal";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function parseEuroInput(raw: string): number {
  const cleaned = raw.replace(/[€\s]/g, "").replace(/\./g, "").replace(/,/, ".");
  const n = Number(cleaned);
  if (!Number.isFinite(n)) throw new Error("invalid_amount");
  return Math.round(n * 100);
}

interface Props {
  open: boolean;
  onClose: () => void;
  existingBalances: readonly BalanceDto[];
  submitting?: boolean;
  errorKey?: string | null;
  onSubmit: (input: { date: string; balanceCents: number; note?: string }) => void;
  onReplace: (
    oldBalanceId: string,
    input: { date: string; balanceCents: number; note?: string },
  ) => void;
}

export function BalanceFormModal({
  open,
  onClose,
  existingBalances,
  submitting,
  errorKey,
  onSubmit,
  onReplace,
}: Props) {
  const { t } = useTranslation();
  const [date, setDate] = useState(todayIso());
  const [amount, setAmount] = useState("0,00");
  const [note, setNote] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDate(todayIso());
      setAmount("0,00");
      setNote("");
      setLocalError(null);
    }
  }, [open]);

  const existing = existingBalances.find((b) => b.date === date) ?? null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    let cents = 0;
    try {
      cents = parseEuroInput(amount);
    } catch {
      setLocalError("accounts.errors.invalid_amount");
      return;
    }
    const payload = { date, balanceCents: cents, note: note.trim() === "" ? undefined : note };
    if (existing) {
      onReplace(existing.id, payload);
    } else {
      onSubmit(payload);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={t("accounts.balance.title")}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="bdate">
            {t("accounts.balance.label.date")}
          </label>
          <input
            id="bdate"
            type="date"
            required
            max={todayIso()}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full border border-slate-300 rounded px-3 py-2"
          />
          {existing && (
            <p className="mt-1 text-xs text-amber-700">
              {t("accounts.balance.duplicate_date_hint", { date: existing.date })}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="bamount">
            {t("accounts.balance.label.amount")}
          </label>
          <input
            id="bamount"
            type="text"
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="1.234,56"
            className="w-full border border-slate-300 rounded px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="bnote">
            {t("accounts.balance.label.note")}
          </label>
          <input
            id="bnote"
            type="text"
            maxLength={200}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full border border-slate-300 rounded px-3 py-2"
          />
        </div>

        {(localError || errorKey) && (
          <p className="text-sm text-red-600">{t(localError ?? errorKey ?? "errors.unknown")}</p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-slate-300 rounded text-sm"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-400 text-white rounded text-sm"
          >
            {existing
              ? t("accounts.balance.replace")
              : submitting
                ? t("common.submitting")
                : t("common.save")}
          </button>
        </div>
      </form>
    </Modal>
  );
}
